import { memo } from "react";
import { C } from "@/lib/colors";
import { useTopBarStats } from "@/lib/useTopBarStats";

/**
 * TopBar live ribbon — mirrors the landing's "we're watching" pulse
 * inside the dashboard chrome (brand sync 2026-05-23). Tells the
 * operator at a glance that the scan loop is alive and where the
 * loudest spread is right now, without forcing them to look down at
 * the table.
 *
 * Format (desktop):
 *   ● Live  ·  N active spreads  ·  top +X.XX % SYM/USDT  ·  last update Ns ago
 *
 * Format (mobile, <700 px):
 *   ● Live  ·  N spreads
 *
 * Visual rules (per the brand-sync token doc):
 *   * ``● Live`` dot is ``--cy`` — "we're live / aware" chrome slot
 *   * Spread % uses ``--mint`` — that's the data slot ("number went up")
 *   * Everything else is mono, ``--text-3`` for labels, ``--text-1``
 *     for emphasised numbers
 *
 * Data: pulled via ``useTopBarStats`` — derived entirely from the
 * existing ``useSpreadsStore`` snapshot, no new endpoint.
 */
export const LiveRibbon = memo(function LiveRibbon() {
  const { activeCount, topSpread, ageS, connected } = useTopBarStats();

  const topPct =
    topSpread && Number.isFinite(Number(topSpread.net_spread_pct))
      ? Number(topSpread.net_spread_pct).toFixed(2)
      : null;
  const topSymbol = topSpread?.symbol || topSpread?.asset?.toUpperCase();
  const ageLabel = ageS == null ? "—" : `${ageS}s`;

  // When the store reports disconnected, fall back to a muted "offline"
  // state — dot greys out, no spread numbers. Avoids the dishonest
  // case where the bar reads "Live" on a stale snapshot from minutes
  // ago after a WS drop.
  const dotColor = connected ? C.cy : C.text4;
  const liveLabel = connected ? "Live" : "Offline";

  return (
    <div
      className="font-mono flex items-center"
      style={{
        gap: 14,
        fontSize: 11,
        color: C.text3,
        letterSpacing: "0.04em",
        // Uniform uppercase across the whole ribbon ("ACTIVE SPREADS", "TOP",
        // "LAST UPDATE … AGO") to match the LIVE/OFFLINE label — numbers and
        // symbols (OSHI, +3266.80 %) are unaffected.
        textTransform: "uppercase",
        minWidth: 0,
        flex: 1,
      }}
    >
      {/* Live badge — cyan pulse + uppercase label, always visible
          (including mobile). The hidden-on-mobile bits below cover
          the more verbose pieces. */}
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          color: connected ? C.cy : C.text4,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          flexShrink: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: dotColor,
            boxShadow: connected ? `0 0 8px ${C.cy}` : "none",
            animation: connected
              ? "pulse 1.6s ease-in-out infinite"
              : "none",
            flexShrink: 0,
          }}
        />
        {liveLabel}
      </span>

      {/* Active spreads count — always visible. Number in text-1
          (page-bright) for the "this is the headline number" effect. */}
      <span style={{ flexShrink: 0 }}>
        <span style={{ color: C.text4 }}>·</span>{" "}
        <span style={{ color: C.text1, fontWeight: 500 }}>{activeCount}</span>{" "}
        <span className="hidden sm:inline">active spreads</span>
        <span className="sm:hidden">spreads</span>
      </span>

      {/* Top spread — hidden on narrow viewports. Mint for the % per
          data-color rule; text-1 for the symbol so the eye reads
          "SOL/USDT +0.71 %" as a single chunk. */}
      {topPct != null && topSymbol && (
        <span className="hidden md:inline" style={{ flexShrink: 0 }}>
          <span style={{ color: C.text4 }}>·</span>{" "}
          <span style={{ color: C.text3 }}>top</span>{" "}
          <span style={{ color: C.mint, fontWeight: 600 }}>
            +{topPct}&nbsp;%
          </span>{" "}
          <span style={{ color: C.text1, fontWeight: 500 }}>{topSymbol}</span>
        </span>
      )}

      {/* Last update — hidden on narrow viewports. Ticks every second
          via the hook's internal interval. */}
      <span className="hidden md:inline" style={{ flexShrink: 0 }}>
        <span style={{ color: C.text4 }}>·</span>{" "}
        <span style={{ color: C.text3 }}>last update</span>{" "}
        <span style={{ color: C.text1, fontWeight: 500 }}>{ageLabel}</span>{" "}
        <span style={{ color: C.text3 }}>ago</span>
      </span>

      {/* pulse keyframe centralised in index.css (plan 001) */}
    </div>
  );
});
